<template>
  <div class="step-information">
    <div class="bg-primary-100 border border-primary-400 text-primary-700 px-4 py-3 rounded relative mb-4 bg-opacity-25" role="alert" v-if="consultationsDisabled.length > 0">
      <span class="block sm:inline">
        As consultas abaixo estão desabilitadas temporariamente, isso fará que a consulta nessas fontes sejam ignoradas:

        <ul class="list-disc pl-4">
          <li v-for="endpoint in consultationsDisabled">{{ endpoint.label }}</li>
        </ul>
      </span>
    </div>

    <div class="fields">
      <information-field
        :name="tagFieldName"
        :type="tag"
        :label="tagFieldLabel"
        :required="true"
        v-model="form[tagFieldName]"
        v-if="hasTagField"
      />

      <information-field
        name="id"
        type="text"
        label="Identificador da Consulta"
        :required="true"
        v-model="form.id"
      />
    </div>

    <div
      class="fields"
      v-for="(group, key) in sharedFieldsChunked"
      :key="key"
    >
      <information-field
        v-for="field in group"
        :key="field.name"
        :type="field.type"
        :name="field.name"
        :label="field.label"
        :tip="field.tip"
        :options="field.values"
        :disabled="disabledFields.includes(field.name)"
        :required="!field.optional"
        v-model="form[field.name]"
      />
    </div>

    <div
      class="group-fields"
      v-for="group in exclusiveFieldsGroupedChunked"
      :key="group.name"
    >
      <legend class="groupTitle">{{ group.label }}</legend>

      <div
        class="fields"
        v-for="(chunk, key) in group.fields"
        :key="key"
      >
        <information-field
          v-for="field in chunk"
          :key="field.name"
          :type="field.type"
          :name="`${field.name}${(group.id ? `#${group.id}` : '')}`"
          :label="field.label"
          :tip="field.tip"
          :options="field.values"
          :disabled="disable(group, field)"
          :required="!field.optional"
          v-model="form[`${field.name}${(group.id ? `#${group.id}` : '')}`]"
        />
      </div>
    </div>
    <loader v-if="loading"/>
  </div>
</template>

<script>
import { chunk, flatMap } from 'lodash';
import newQueryFormFields from '@/compliance/mixins/newQueryFormFields';
import newQueryTagFields from '@/compliance/mixins/newQueryTagField';
import InformationField from '@/compliance/components/new-query/step-information/InformationField.vue';
import Loader from '@/support/components/Loader.vue';
import { mapState } from 'vuex';

export default {
  name: 'StepInformation',

  props: {
    tag: {
      type: String,
      required: true,
    },
    form: {
      type: Object,
      required: true,
    },
  },

  model: {
    prop: 'form',
    event: 'input',
  },

  data() {
    return {
      disabledFields: [],
      loading: false,
    };
  },

  methods: {
    /**
     * Load disabled fields.
     */
    loadDisabledFields() {
      if (this.form.consultations.length > 0) {
        const exclusiveFields = flatMap(this.exclusiveFieldsGrouped, (group) => group.fields);

        this.disabledFields = [...this.sharedFields, ...exclusiveFields]
          .filter((field) => field.oneOf)
          .filter((field) => {
            const disabled = field.oneOf.filter((rule) => {
              const fieldValue = this.form[rule.field];
              if (!fieldValue) {
                return false;
              }

              return !rule.values || rule.values.some((value) => value === fieldValue || parseInt(value, 10) === parseInt(fieldValue, 10));
            }).length === 0;

            return disabled;
          })
          .map((field) => field.name);
      } else {
        const exclusiveFields = flatMap(this.exclusiveFieldsGrouped, (group) => group.fields.filter((field) => field.oneOf)
          .filter((field) => field.type === 'select' || field.type === 'radio')
          .map((field) => `${field.name}#${group.id}`));
        this.disabledFields = [...exclusiveFields];
      }
    },

    async loadTemplate() {
      if (this.form.template.id) {
        this.loadValuesFromTemplate();
      }
    },

    loadValuesFromTemplate() {
      this.template.consultations
        .forEach((consultation) => consultation.fields.forEach((field) => {
          this.form[`${field.name}#${consultation.id}`] = field.value;
        }));
    },

    disable(group, field) {
      if (this.form.consultations.length > 0) {
        return this.disabledFields.includes(field.name);
      }

      if (!!this.form[`trf4-certidao-regional/_email_senha${(group.id ? `#${group.id}` : '')}`] && `${field.name}${(group.id ? `#${group.id}` : '')}`.startsWith('trf4-certidao-regional/email')) {
        return false;
      }
      if (!!this.form[`trf4-certidao-regional/_email_senha${(group.id ? `#${group.id}` : '')}`] && `${field.name}${(group.id ? `#${group.id}` : '')}`.startsWith('trf4-certidao-regional/senha')) {
        return false;
      }
      if (!this.form[`trf4-certidao-regional/_email_senha${(group.id ? `#${group.id}` : '')}`] && `${field.name}${(group.id ? `#${group.id}` : '')}`.startsWith('trf4-certidao-regional/email')) {
        return true;
      }
      if (!this.form[`trf4-certidao-regional/_email_senha${(group.id ? `#${group.id}` : '')}`] && `${field.name}${(group.id ? `#${group.id}` : '')}`.startsWith('trf4-certidao-regional/senha')) {
        return true;
      }

      if (this.disabledFields.includes(`${field.name}${(group.id ? `#${group.id}` : '')}`)) {
        return true;
      }
      if (!!group.id && !!this.form[`${field.name}${(group.id ? `#${group.id}` : '')}`] && !`${field.name}${(group.id ? `#${group.id}` : '')}`.startsWith('trf4-certidao-regional/_email_senha')) {
        return true;
      }

      return false;
    },
  },

  computed: {
    /**
     * The shared fields chunked in
     * group of three.
     *
     * @returns {Array}
     */
    sharedFieldsChunked() {
      return chunk(this.sharedFields, 3);
    },

    consultationsDisabled() {
      if (this.form.template.id) {
        return this.template.endpoints.filter((e) => e.enable === false);
      }

      return [];
    },

    /**
     * The exclusives fields grouped and chunked in
     * group of three.
     *
     * @returns {Array}
     */
    exclusiveFieldsGroupedChunked() {
      return this.exclusiveFieldsGrouped
        .map((group) => ({
          ...group,
          fields: chunk(group.fields, 3),
        }));
    },

    ...mapState('template', {
      template: 'template',
    }),
  },

  async mounted() {
    await this.loadTemplate();
    this.loadDisabledFields();
  },

  watch: {
    form: {
      deep: true,
      handler() {
        this.loadTemplate().then(this.loadDisabledFields);
      },
    },

    disabledFields(newDisabledFields) {
      if (this.form.consultations.length > 0) {
        newDisabledFields.forEach((field) => {
          delete this.form[field];
        });
      }
    },
  },

  mixins: [
    newQueryFormFields,
    newQueryTagFields,
  ],

  components: {
    InformationField,
    Loader,
  },
};
</script>

<style lang="scss" scoped>
  .step-information {
    width: 100%;

    @media (min-width: 768px) {
      @apply grid;
      @apply grid-cols-1;
      @apply gap-4;
    }

    > .fields,
    > .group-fields > .fields {
      width: 100%;

      @media (min-width: 768px) {
        @apply grid;
        @apply gap-4;
        grid-auto-columns: 1fr;
        grid-auto-flow: dense;
        grid-template-columns: repeat( auto-fit, minmax(250px, 1fr) );
      }
    }
  }
</style>
